import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

const STATUS_STYLES = {
  new:     'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
  active:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  dormant: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
    {status}
  </span>
);

const Delta = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  if (value === 0) return <span className="text-gray-400 dark:text-gray-500">±0</span>;
  return (
    <span className={value > 0 ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
      {value > 0 ? `+${value}` : value}
    </span>
  );
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}yr ago`;
}

const SORT_OPTIONS = [
  { value: 'bookmarkCount', label: 'Bookmarks' },
  { value: 'lastActivity',  label: 'Last Active' },
  { value: 'registeredAt', label: 'Registered' },
  { value: 'tagCount',      label: 'Tags' },
];

const AdminUsers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('bookmarkCount');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookmarkService.getAdminUserStats();
      setData(result);
    } catch {
      setError('Failed to load user stats. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sortedUsers = useMemo(() => {
    if (!data?.users) return [];
    let users = [...data.users];
    if (search.trim()) {
      const q = search.toLowerCase();
      users = users.filter(u => u.email.toLowerCase().includes(q));
    }
    users.sort((a, b) => {
      if (sortBy === 'lastActivity' || sortBy === 'registeredAt') {
        const av = a[sortBy] || '';
        const bv = b[sortBy] || '';
        return bv.localeCompare(av);
      }
      return (b[sortBy] || 0) - (a[sortBy] || 0);
    });
    return users;
  }, [data, sortBy, search]);

  const summary = data?.summary;

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Stats</h1>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 mb-6">
          {error}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Total Users',    value: summary.totalUsers,      color: 'text-gray-900 dark:text-gray-100' },
            { label: 'Active 7d',      value: summary.activeUsers7d,   color: 'text-green-600' },
            { label: 'Active 30d',     value: summary.activeUsers30d,  color: 'text-primary-600' },
            { label: 'New this week',  value: summary.newUsers7d,      color: 'text-indigo-600' },
            { label: 'Total Bookmarks',value: summary.totalBookmarks,  color: 'text-gray-900 dark:text-gray-100' },
            { label: 'Pipeline Issues',value: summary.processingIssues,color: summary.processingIssues > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 text-center">
              <p className={`text-2xl font-bold ${color}`}>{value ?? '—'}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by email…"
            className="form-input text-sm py-1.5 w-52"
          />
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500 dark:text-gray-400">Sort by</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {data?.capturedAt && (
            <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:block">
              as of {new Date(data.capturedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>

        {loading ? (
          <div className="space-y-2 p-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
            ))}
          </div>
        ) : sortedUsers.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">
            {search ? 'No users match that filter.' : 'No user data yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Registered</th>
                  <th className="px-4 py-3 text-right">Bookmarks</th>
                  <th className="px-4 py-3 text-right" title={`Change since snapshot ${data?.summary?.snapshotDate || 'yesterday'}`}>
                    Delta
                  </th>
                  <th className="px-4 py-3 text-right">Tags</th>
                  <th className="px-4 py-3">Last Active</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {sortedUsers.map(user => (
                  <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-gray-800 dark:text-gray-200 font-medium truncate max-w-xs block">{user.email}</span>
                      {user.processingCount > 0 && (
                        <span className="text-xs text-red-500">{user.processingCount} stuck</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{fmtDate(user.registeredAt)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">{user.bookmarkCount}</td>
                    <td className="px-4 py-3 text-right"><Delta value={user.delta} /></td>
                    <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{user.tagCount}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400" title={user.lastActivity ? new Date(user.lastActivity).toLocaleString() : ''}>
                      {fmtRelative(user.lastActivity)}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={user.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && sortedUsers.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
            {sortedUsers.length} user{sortedUsers.length !== 1 ? 's' : ''}
            {summary?.snapshotDate && ` · delta vs snapshot ${summary.snapshotDate}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
